import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { take } from 'lodash-es';
import { Observable } from 'rxjs';
import { filter, first, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { DocumentActions } from './document.actions';
import { FILE_UPLOAD_LIMIT } from '../../features/document/constants/document-upload-limit';
import { TransactionDocumentService } from '../../features/document/services/transaction-document.service';
import {
  FAIL_NOTIFICATION_CONFIGURATION,
  SUCCESS_NOTIFICATION_CONFIGURATION,
} from '../../features/shared/constants/notification-configurations';
import { Permission } from '../../features/shared/models/api/permissions.enum';
import { FileUploadResult } from '../../features/shared/services/file/file.service';
import { PermissionService } from '../../features/shared/services/permission.service';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { AppActions } from '../app/app.actions';
import { DocumentListActions } from '../document-list/document-list.actions';
import { RootState } from '../root-state.model';

@Injectable()
export class DocumentEffects {
  private service = inject(TransactionDocumentService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly permissionService = inject(PermissionService);

  loadFlashExtractionStatsEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.LoadFlashExtractionStats),
      switchMap(() => this.permissionService.hasEveryPermission([Permission.WRITE_FLASH_EXTRACTION]).pipe(first())),
      filter((hasPermission) => hasPermission),
      switchMap(() =>
        this.service.getFlashExtractionStats().pipe(
          map((stats) => DocumentActions.LoadFlashExtractionStatsSuccess({ stats })),
          reportEffectError(DocumentActions.LoadFlashExtractionStatsFail)
        )
      )
    )
  );

  filesAddedEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.FilesAdded, DocumentActions.FileFromUrlAdded),
      withLatestFrom(this.store),
      filter(([action, store]) => store.document.files.some((f) => f.uploadStatus === FileUploadResult.NOT_STARTED)),
      map(([action, store]) =>
        DocumentActions.LoadFile({
          file: store.document.files.find((f) => f.uploadStatus === FileUploadResult.NOT_STARTED),
        })
      )
    )
  );

  loadFileEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.LoadFile),
      withLatestFrom(this.store),
      mergeMap(([action, store]) => {
        return this.service.uploadFile(action.file).pipe(
          filter(({ uploadStatus }) => [FileUploadResult.COMPLETED, FileUploadResult.FAILED].includes(uploadStatus)),
          map(({ uploadStatus, fileOverwrite }) =>
            DocumentActions.LoadFileSuccess({
              file: { ...action.file, ...fileOverwrite, uploadStatus, createdAt: new Date().toISOString() },
            })
          )
        );
      })
    )
  );

  loadFileFailedEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.LoadFileSuccess),
      filter((action) => action.file?.uploadStatus === FileUploadResult.FAILED),
      map((action) =>
        AppActions.PushNotification({
          message: `Failed to upload the ${action.file?.file?.name} document. Please remove it from the list and try again.`,
          config: FAIL_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  loadFileSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.LoadFileSuccess),
      withLatestFrom(this.store),
      filter(([action, store]) => store.document.files.some((f) => f.uploadStatus === FileUploadResult.NOT_STARTED)),
      map(([action, store]) =>
        DocumentActions.LoadFile({
          file: store.document.files.find((f) => f.uploadStatus === FileUploadResult.NOT_STARTED),
        })
      )
    )
  );

  deleteFileEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.DeleteFile),
      filter((action) => action.file.uploadStatus !== FileUploadResult.SKIPPED),
      map((action) =>
        DocumentListActions.DeleteItem({
          document: action.file,
        })
      )
    )
  );

  submitFilesEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.SubmitFiles),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        return this.service
          .submitUploadedDocuments(
            take(store.document.files, FILE_UPLOAD_LIMIT)
              .filter((f) => f.uploadStatus === FileUploadResult.COMPLETED)
              .map((f) => ({
                uuid: f.uuid,
                size: f.file?.size || f.fileFromUrl?.size,
                flashExtractionRequested: f.flashExtractionRequested,
              })),
            action.message,
            action.assetShortName,
            action.libraryShortName,
            action.bundleId
          )
          .pipe(
            map(() => DocumentActions.SubmitFilesSuccess()),
            reportEffectError(DocumentActions.SubmitFilesFail)
          );
      })
    )
  );

  submitFilesSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.SubmitFilesSuccess),
      map((action) =>
        AppActions.PushNotification({
          message: 'Documents are submitted for analysis',
          config: SUCCESS_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  submitFilesFailEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.SubmitFilesFail),
      map((action) =>
        AppActions.PushNotification({
          message: 'Failed to submit the documents',
          config: FAIL_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );
}
